/**
 * The current step of a batch (#2636, ADR-0044 decisions 3 and 5). Pure: no
 * React, no i18n. Every rule reads the tenant flow, never an HGS step name.
 *
 * A step is overdue (decision 3, daily anchor only) when it has jobs and a
 * passed deadline asks for a later step; a job already at the deadline's
 * `reach` is on time. The current step is the first in-flight step with jobs,
 * skipping a straggler: an overdue step while a later step still has jobs.
 */
import { wallTimeToUtcIso } from '@resource-kit/controllers';
import { rowSize, type BoardDay, type BoardStage } from './boardModel';
import { toBoardStage, toBoardStages, type DeliveryFlowWire } from './boardWire';

export type FlowGateKind = DeliveryFlowWire['gates'][number]['kind'];
export type FlowGateCategory = DeliveryFlowWire['gates'][number]['category'];
export interface FlowDeadline {
  kind: FlowGateKind;
  localTime: string;
  reach: BoardStage;
  category: FlowGateCategory;
}
export interface FlowStepModel {
  /** False when the flow has no batch key (decision 5): no current step. */
  batched: boolean;
  /** Step order = flow.columns, mapped through toBoardStages. */
  steps: readonly BoardStage[];
  /** Steps that are the `from` of some flow action, in step order; every other step is terminal. */
  inFlight: readonly BoardStage[];
  /** Gates with a non-null reach, in flow.gates order; reach-null gates never make a job overdue. */
  deadlines: readonly FlowDeadline[];
  timezone: string;
}

const BATCHED = { shipping_date: true } as const satisfies Record<
  DeliveryFlowWire['batch'],
  boolean
>;

export function flowStepModel(flow: DeliveryFlowWire): FlowStepModel {
  const steps = toBoardStages(flow.columns);
  const from = new Set(flow.actions.map((action) => toBoardStage(action.from)));
  const deadlines: FlowDeadline[] = [];
  for (const gate of flow.gates) {
    if (gate.reach === null) continue;
    deadlines.push({
      kind: gate.kind,
      localTime: gate.local_time,
      reach: toBoardStage(gate.reach),
      category: gate.category,
    });
  }
  return {
    batched: BATCHED[flow.batch],
    steps,
    inFlight: steps.filter((step) => from.has(step)),
    deadlines,
    timezone: flow.timezone,
  };
}

/** Unix ms of a deadline on a batch date (merchant wall clock). */
export function deadlineMs(model: FlowStepModel, date: string, deadline: FlowDeadline): number {
  return Date.parse(wallTimeToUtcIso(`${date}T${deadline.localTime}:00.000`, model.timezone));
}

/**
 * The overdue bar: the furthest step a passed deadline asks for, as a step
 * index; -1 when none has passed. A step before the bar is overdue.
 */
function overdueBar(model: FlowStepModel, date: string, nowMs: number): number {
  // `at == now` counts as passed, as in the backend's `nextBoardGate`.
  return Math.max(
    -1,
    ...model.deadlines
      .filter((d) => nowMs >= deadlineMs(model, date, d))
      .map((d) => model.steps.indexOf(d.reach)),
  );
}

/** In-flight steps with jobs whose every job is overdue (decision 3, daily anchor only). */
export function overdueSteps(
  model: FlowStepModel,
  day: BoardDay,
  nowMs: number,
): ReadonlySet<BoardStage> {
  const bar = overdueBar(model, day.date, nowMs);
  return new Set(model.inFlight.filter((s) => day.counts[s] > 0 && model.steps.indexOf(s) < bar));
}

export type CurrentStep = BoardStage | 'closed' | 'none';

/**
 * Decision 5. `null` = batch key none: not computed. The current step is the
 * first step with in-flight jobs that is not overdue; an overdue step is a
 * straggler only while a later step has in-flight jobs, so the last one stays.
 */
export function currentStep(
  model: FlowStepModel,
  day: BoardDay,
  nowMs: number,
): CurrentStep | null {
  if (!model.batched) return null;
  if (rowSize(day) === 0) return 'none'; // six-column sum, same as rowCopy
  const active = model.inFlight.filter((s) => day.counts[s] > 0);
  const last = active[active.length - 1];
  if (last === undefined) return 'closed';
  const bar = overdueBar(model, day.date, nowMs);
  return active.find((s) => model.steps.indexOf(s) >= bar) ?? last;
}

export interface Straggler {
  step: BoardStage;
  count: number;
  deadline: FlowDeadline;
}

/**
 * Decision 5: the earliest straggler step and the deadline that decides it, or
 * null. The deciding deadline is the earliest passed one that asks for a step
 * after the straggler (a tie keeps the earlier one in `flow.gates`); the card's
 * time and category both come from it.
 */
export function earliestStraggler(
  model: FlowStepModel,
  day: BoardDay,
  nowMs: number,
): Straggler | null {
  const current = currentStep(model, day, nowMs);
  if (current === null || current === 'none' || current === 'closed') return null;
  const step = model.inFlight.find((s) => day.counts[s] > 0);
  if (step === undefined || step === current) return null;
  const i = model.steps.indexOf(step);
  let decided: { deadline: FlowDeadline; ms: number } | null = null;
  for (const deadline of model.deadlines) {
    const ms = deadlineMs(model, day.date, deadline);
    const passed = nowMs >= ms && i < model.steps.indexOf(deadline.reach);
    // Strictly earlier: a tie keeps the one first in `flow.gates`.
    if (passed && (decided === null || ms < decided.ms)) decided = { deadline, ms };
  }
  // Never null in practice: `step` was skipped, so a passed deadline makes it overdue.
  if (decided === null) return null;
  return { step, count: day.counts[step], deadline: decided.deadline };
}
